/**
 * <s-area-switcher>
 * Wraps the partner areas of the Share page. Clicking a link with
 * [data-share-area="<id>"] moves that area to the top of the list (CSS order),
 * keeps the others in their original order below, and scrolls to it.
 * Without JavaScript the links stay plain in-page anchors.
 */
class AreaSwitcher extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    document.addEventListener('click', (event) => this.onClick(event), { signal: this.ac.signal });

    this.bar = this.querySelector('[data-share-bar]');
    const tiles = document.querySelector('[data-share-tiles]');
    if (this.bar && tiles && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.bar.toggleAttribute('data-visible', scrolledPast);
      });
      this.observer.observe(tiles);
    } else {
      this.bar?.setAttribute('data-visible', '');
    }
    requestAnimationFrame(() => this.openFromHash());
  }

  disconnectedCallback() {
    this.ac?.abort();
    this.observer?.disconnect();
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-share-area]');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (this.activate(link.dataset.shareArea)) event.preventDefault();
  }

  /** Opens the area named in the URL hash (e.g. /pages/kunden-partner#gutachter) on arrival. */
  openFromHash() {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id && document.querySelector(`[data-share-area="${CSS.escape(id)}"]`)) this.activate(id);
  }

  activate(id) {
    const area = this.querySelector(`#${CSS.escape(id)}`);
    if (!area) return false;

    let item = area;
    while (item.parentElement && item.parentElement !== this) item = item.parentElement;

    for (const child of this.children) {
      if (child instanceof HTMLElement && child.tagName !== 'NAV') child.style.order = child === item ? '-1' : '';
    }
    document.querySelectorAll('[data-share-area]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.shareArea === id) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    area.setAttribute('tabindex', '-1');
    area.focus({ preventScroll: true });
    return true;
  }
}

if (!customElements.get('s-area-switcher')) {
  customElements.define('s-area-switcher', AreaSwitcher);
}
